import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { isAdminRole } from '../../utils/roleHelpers';

const PerformanceTab = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profilingEnabled, setProfilingEnabled] = useState(false);
  const [slowQueries, setSlowQueries] = useState([]);
  const [profilingLoading, setProfilingLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [currentThreshold, setCurrentThreshold] = useState('100ms'); // Track current threshold

  const { user } = useSelector(state => state.auth);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (user?.role === 'platform_admin') {
      fetchPerformanceStats();
      checkProfilingStatus();
      const interval = setInterval(() => {
        fetchPerformanceStats();
        if (profilingEnabled) {
          fetchSlowQueries();
        }
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, profilingEnabled]);

  const checkProfilingStatus = async () => {
    try {
      const response = await axios.get('/api/performance/profiling/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setProfilingEnabled(response.data.enabled);
      if (response.data.enabled) {
        fetchSlowQueries();
      }
    } catch (err) {
      console.error('Failed to check profiling status:', err);
    }
  };

  const fetchSlowQueries = async () => {
    try {
      const response = await axios.get('/api/performance/slow-queries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSlowQueries(response.data.queries || []);
      
      // Update threshold from API response
      if (response.data.threshold) {
        setCurrentThreshold(response.data.threshold);
      }
    } catch (err) {
      console.error('Failed to fetch slow queries:', err);
    }
  };

  const fetchPerformanceStats = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get('/api/performance/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setPerformanceData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch performance data');
      console.error('Performance fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const enableQueryProfiling = async () => {
    setProfilingLoading(true);
    try {
      const response = await axios.post('/api/performance/profiling', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setProfilingEnabled(true);
      setError('');
      
      // Show success message with details
      const message = `✅ ${response.data.message}\n📊 Threshold: ${response.data.threshold}\n🔍 Slow queries will appear below`;
      alert(message);
      
      // Fetch initial slow queries after a short delay
      setTimeout(() => {
        fetchSlowQueries();
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable profiling');
      console.error('Profiling enable error:', err);
    } finally {
      setProfilingLoading(false);
    }
  };

  const disableQueryProfiling = async () => {
    setProfilingLoading(true);
    try {
      await axios.delete('/api/performance/profiling', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setProfilingEnabled(false);
      setSlowQueries([]);
      setError('');
      alert('🛑 Query profiling disabled');
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable profiling');
      console.error('Profiling disable error:', err);
    } finally {
      setProfilingLoading(false);
    }
  };

  const testSlowQuery = async () => {
    if (process.env.NODE_ENV === 'production') {
      alert('Test queries not available in production');
      return;
    }
    
    try {
      const response = await axios.post('/api/performance/test-slow-query', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Create detailed message with test results
      let message = `🧪 ${response.data.message}\n`;
      message += `⏰ Total Duration: ${response.data.duration}\n`;
      message += `🎯 Threshold: ${response.data.threshold}\n\n`;
      message += `� Individual Tests:\n`;
      
      if (response.data.tests) {
        response.data.tests.forEach(test => {
          message += `   • ${test.type}: ${test.duration}\n`;
        });
      }
      
      message += `\n�🔍 Check slow queries section below in a few seconds`;
      
      alert(message);
      
      // Refresh slow queries after a short delay
      setTimeout(() => {
        fetchSlowQueries();
      }, 2000);
      
    } catch (err) {
      console.error('Test query error:', err);
      alert('Failed to execute test query');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  if (user?.role !== 'platform_admin') {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <strong>Access Denied:</strong> Only administrators can view performance metrics.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#054581] mb-2">Database Performance</h2>
          <p className="text-gray-600">Monitor MongoDB performance and query optimization</p>
        </div>
        
        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
          <button
            onClick={fetchPerformanceStats}
            disabled={loading}
            className="flex-1 sm:flex-initial py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] disabled:opacity-50 transition-colors"
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh Data'}
          </button>
          
          {!profilingEnabled ? (
            <button
              onClick={enableQueryProfiling}
              disabled={profilingLoading}
              className="flex-1 sm:flex-initial py-2 px-4 bg-yellow-500 text-white rounded-md font-medium hover:bg-yellow-600 disabled:opacity-50 transition-colors"
            >
              {profilingLoading ? '⏳ Enabling...' : '📊 Enable Profiling'}
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={fetchSlowQueries}
                className="flex-1 sm:flex-initial py-2 px-4 bg-green-500 text-white rounded-md font-medium hover:bg-green-600 transition-colors"
              >
                🔍 Refresh Queries
              </button>
              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={testSlowQuery}
                  className="flex-1 sm:flex-initial py-2 px-4 bg-purple-500 text-white rounded-md font-medium hover:bg-purple-600 transition-colors"
                >
                  🧪 Test Slow Query
                </button>
              )}
              <button
                onClick={disableQueryProfiling}
                disabled={profilingLoading}
                className="flex-1 sm:flex-initial py-2 px-4 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {profilingLoading ? '⏳ Disabling...' : '🛑 Disable Profiling'}
              </button>
            </div>
          )}
        </div>
      </div>

      {lastRefresh && (
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleString()}
        </p>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Performance Data */}
      {performanceData && (
        <>
          {/* Database Overview */}
          <div className="bg-white border border-[#1086a4] rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-[#054581] mb-4">📊 Database Overview</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="text-2xl font-bold text-[#054581]">
                  {performanceData.database?.collections || 0}
                </div>
                <div className="text-sm text-gray-600">Total Collections</div>
              </div>
              
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <div className="text-2xl font-bold text-[#054581]">
                  {formatBytes(performanceData.database?.dataSize || 0)}
                </div>
                <div className="text-sm text-gray-600">Data Size</div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <div className="text-2xl font-bold text-[#054581]">
                  {formatBytes(performanceData.database?.indexSize || 0)}
                </div>
                <div className="text-sm text-gray-600">Index Size</div>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <div className="text-2xl font-bold text-[#054581]">
                  {formatBytes(performanceData.database?.storageSize || 0)}
                </div>
                <div className="text-sm text-gray-600">Storage Size</div>
              </div>
            </div>
          </div>

          {/* Collections Performance */}
          <div className="bg-white border border-[#1086a4] rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-[#054581] mb-4">📈 Collection Performance</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Collection
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Indexes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {performanceData.collections && Object.entries(performanceData.collections).map(([name, stats]) => (
                    <tr key={name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#054581]">
                        {name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatNumber(stats.count || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatBytes(stats.size || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {stats.indexCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          stats.indexCount > 3 
                            ? 'bg-green-100 text-green-800' 
                            : stats.indexCount > 1 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.indexCount > 3 ? 'Optimized' : stats.indexCount > 1 ? 'Good' : 'Needs Indexes'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Slow Queries Section */}
          {profilingEnabled && (
            <div className="bg-white border border-[#1086a4] rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#054581]">🐌 Slow Queries (&gt;{currentThreshold})</h3>
                <div className="flex items-center space-x-2">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                    Profiling Active
                  </span>
                  <button
                    onClick={fetchSlowQueries}
                    className="py-1 px-3 text-sm bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] transition-colors"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>
              
              {slowQueries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Collection
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Operation
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration (ms)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Query
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plan
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {slowQueries.map((query, index) => (
                        <tr key={index} className={query.duration > 500 ? 'bg-red-50' : query.duration > 200 ? 'bg-yellow-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(query.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#054581]">
                            {query.collection}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {query.operation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              query.duration > 500 
                                ? 'bg-red-100 text-red-800' 
                                : query.duration > 200 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {query.duration}ms
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {query.query.length > 50 ? `${query.query.substring(0, 50)}...` : query.query}
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {query.planSummary}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">🎉 No slow queries detected</div>
                  <div className="text-sm text-gray-500">
                    Your database is performing well! Queries taking longer than {currentThreshold} will appear here.
                  </div>
                </div>
              )}
              
              {/* Query Performance Tips */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-[#054581] mb-2">🚀 Query Optimization Tips:</h4>
                <div className="text-xs text-gray-700 space-y-1">
                  <div>• <strong>Red queries (&gt;500ms):</strong> Critical - Need immediate optimization</div>
                  <div>• <strong>Yellow queries (&gt;200ms):</strong> Warning - Consider adding indexes</div>
                  <div>• <strong>Green queries (100-200ms):</strong> Good - Monitor for trends</div>
                  <div>• <strong>COLLSCAN plans:</strong> Indicate missing indexes</div>
                  <div>• <strong>IXSCAN plans:</strong> Using indexes properly</div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-4">💡 Performance Tips</h3>
            
            <div className="space-y-2 text-sm text-gray-700">
              <div>• Collections with 5+ indexes are well-optimized for query performance</div>
              <div>• Enable query profiling to monitor slow queries (&gt;{currentThreshold})</div>
              <div>• Index size should be reasonable relative to data size</div>
              <div>• Regular monitoring helps identify performance bottlenecks</div>
              <div>• Use the refresh button to get real-time performance data</div>
            </div>
          </div>
        </>
      )}

      {/* Loading State */}
      {loading && !performanceData && (
        <div className="bg-white border border-[#1086a4] rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
            <span className="ml-2">Loading performance data...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceTab;
